// @flow

import React, { PureComponent } from 'react';
import type { Element } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { connect } from 'react-redux';

// Helpers
import classNames from 'classnames';
import Button from 'Common/Button';
import Label from 'Common/Label';
import styles from 'components/Form.scss';
import Spinner from 'Common/Spinner';
import { gtmSubmitForm } from 'Common/gtmPayloads';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import axios from 'axios';
import { gtmEventPush } from 'utils/gtm/events';
import * as Actions from 'actionTypes';

import ReCAPTCHA from 'react-google-recaptcha';

const recaptchaRef: {| current: any |} = React.createRef();

/**
 * Form React component.
 */
class Form extends PureComponent<*, *> {
  validationRules: Object;

  /**
   *  Props implementation.
   */
  static propTypes = {
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    buttonText: PropTypes.string,
    classFormValidationMessage: PropTypes.string,
    sizeButton: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    item: PropTypes.object,
    formRef: PropTypes.object,
    successText: PropTypes.string,
    title: PropTypes.string,
    errorText: PropTypes.string,
    handleSubmit: PropTypes.func,
    formType: PropTypes.string.isRequired,
    dispatch: PropTypes.func,
    submissionRender: PropTypes.func
  };
  /**
   *  Props implementation.
   */
  static defaultProps: Object = {
    buttonText: 'Submit',
    sizeButton: 'large',
    successText: `Thanks for your enquiry! It has been sent to the seller, who will be in touch shortly.`,
    errorText: `Something went wrong with the form. Please try again later.`
  };

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    // Defines the validation rules and correspondant messages.
    this.validationRules = {
      required: {
        rule: /^(?!\s*$).+/,
        message: 'This field is required.'
      },
      number: {
        rule: /^(\s*|\d+)$/,
        message: 'Please insert a valid numeric value'
      },
      email: {
        rule: /^(\s*|[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{1,63}))$/,
        message: 'Please insert a valid email address'
      },
      postcode: {
        rule: /^(([gG][iI][rR] {0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?[0-9][0-9]?)|(([a-pr-uwyzA-PR-UWYZ][0-9][a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y][0-9][abehmnprv-yABEHMNPRV-Y]))) {0,}[0-9][abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/,
        message: 'Not a valid postcode format'
      }
    };

    this.state = {
      validationState: this.setValidationState(props.children),
      isFormValid: null,
      inputValues: {},
      formSubmitSuccess: null
    };
  }

  buildInputValuesFromChildren: (children: any) => Object = children => {
    // Build inputValues based on the child components in the form.
    const inputValuesToSet = {};

    const processChildren = children => {
      React.Children.toArray(children)
        .filter(i => i && i.type !== 'p' && i.props.type !== 'radio')
        .map(child => {
          if (child.props.children) {
            processChildren(child.props.children);
          } else {
            const { name, type, value } = child.props;
            // Take any previous user inputs if we have them.
            if (name in this.state.inputValues) {
              inputValuesToSet[name] = this.state.inputValues[name] || value;
            }
            // Otherwise set some defaults.
            else {
              inputValuesToSet[name] = type === 'checkbox' ? false : value;
            }
          }
        });
    };

    processChildren(children);

    return inputValuesToSet;
  };

  componentDidMount() {
    // This will set initial values, so we can guarantee we always have an inputValue for each element
    // instead of waiting for user interaction.
    this.setState({
      inputValues: this.buildInputValuesFromChildren(this.props.children)
    });
  }

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillReceiveProps implementation.
   * @param {object} nextProps object
   */
  UNSAFE_componentWillReceiveProps(nextProps: Object) {
    if (this.props.item.sourceId !== nextProps.item.sourceId) {
      this.setState({
        validationState: this.setValidationState(nextProps.children, true),
        isFormValid: null,
        inputValues: {},
        formSubmitSuccess: null
      });
    }
    // Build inputValues and validation state based on just the child components in the new form.
    else {
      this.setState({
        inputValues: this.buildInputValuesFromChildren(nextProps.children),
        validationState: this.setValidationState(nextProps.children)
      });
    }
  }

  setValidationState: (
    children: any,
    resetExistingValidation?: boolean
  ) => Object = (children, resetExistingValidation = false) => {
    let validationState = {};

    const processChildren = children => {
      React.Children
        // Filter out null values (conditional fields).
        .toArray(children)
        .filter(i => i && i.type !== 'p' && i.props && i.props.type !== 'radio')
        .map(child => {
          if (child.props.children) {
            processChildren(child.props.children);
          } else {
            let validationType = [];
            // Element can be validated according to multiple types of validations.
            // The validationType is an array that contains all validation that needs to get applied to the element
            if (child.props.required) {
              validationType.push('required');
            }
            if (child.props.valType) {
              validationType.push(child.props.valType);
            }

            // Re-use current validations if present, but skip elements whose required flag has changed.
            const currentValidationState =
              this.state &&
              this.state.validationState[child.props.name] &&
              this.state.validationState[child.props.name]
                .isValidationRequired === child.props.required &&
              // This is purposely the same as 2 lines above, as we want to actually return it.
              this.state.validationState[child.props.name];
            // TODO: should we re-validate elements that have changed / re-validate the whole form?
            // Seems like it's not worth the effort and a proper form refactor would be better...

            // Defines the validation state of the form. Foreach children element (i.e input,textarea etc)
            // an initial object gets created to track the element validity.
            validationState[child.props.name] =
              currentValidationState && !resetExistingValidation
                ? currentValidationState
                : {
                    isValid: !!child.props.value || null,
                    isValidationRequired: child.props.required || false,
                    validationMessage: '',
                    isValidated: false,
                    validationType
                  };
          }
        });
    };

    processChildren(children);
    return validationState;
  };

  /**
   * It gets the input's value and pass it to onChange
   * @param {object} event - current event
   * @return {void}
   */
  handleOnChange: (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => void = event => {
    const target: HTMLInputElement = event.target;
    const name = target.name;
    let value = target.value;

    if (target.type === 'checkbox') {
      // This is the value that needs to validate. If the input type is
      // a checkbox the only validation we can run is the 'required'
      // validation. So we can set the value to 'checked' ie not empty
      // or empty for the required validation to be effective.
      value = target.checked ? 'checked' : '';
    }
    if (this.state.isFormValid === false) {
      this.checkInputValidity(name, value);
    }

    this.setState({
      inputValues: {
        ...this.state.inputValues,
        [name]: target.type === 'checkbox' ? target.checked : value
      }
    });
  };

  /**
   * It gets the input's value and pass it to onChange
   * @param {object} event - current event
   * @return {void}
   */
  handleOnBlur: (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => void = event => {
    const target = event.target;
    const name = target.name;
    let value = target.value;

    gtmEventPush({
      method: 'enquiryFormValuesBlur',
      args: {
        callbackOrMessage: this.props.formType,
        itemLabel: name
      }
    });

    if (target.type === 'checkbox') {
      // This is the value that needs to validate. If the input type is
      // a checkbox the only validation we can run is the 'required'
      // validation. So we can set the value to 'checked' ie not empty
      // or empty for the required validation to be effective.
      value = target.checked ? 'checked' : '';
    }

    this.checkInputValidity(name, value);
  };

  /**
   * This function check the validity of an input .
   * @param {string} name - current input's name
   * @param {number|string} value - current input's value
   * @return {void}
   */
  checkInputValidity(name: string, value: mixed): boolean | void {
    let validationState: Object = JSON.parse(
      JSON.stringify(this.state.validationState)
    );
    const valRules = this.state.validationState[name].validationType.length
      ? this.state.validationState[name].validationType
      : null;
    if (!valRules) {
      return false;
    }
    valRules.some(validation => {
      const patt = this.validationRules[validation].rule;
      validationState[name].isValidated = true;
      if (!patt.test(value)) {
        validationState[name].isValid = false;
        const valRule = this.validationRules[validation];
        validationState[name].validationMessage = valRule.message;
        return true;
      } else {
        validationState[name].validationMessage = '';
        validationState[name].isValid = true;
      }
    });

    if (
      JSON.stringify(validationState) !==
      JSON.stringify(this.state.validationState)
    ) {
      this.setState({ validationState });
    }
  }

  /**
   * This function checks the Form validity.
   * @return {void}
   */
  checkFormValidity(): boolean | void {
    let validationState: Object = JSON.parse(
      JSON.stringify(this.state.validationState)
    );
    let isFormValid = true;
    // Check if all form is valid.
    R.keys(validationState).map(key => {
      // Check if any non validated field needs validation.
      let el = validationState[key];
      if (el.isValid !== true && el.isValidationRequired) {
        // If at least one field is not valid the overall Form is not valid.
        // All the required fields need to be higlighted.
        isFormValid = false;
        el.isValid = false;
        el.validationMessage = this.validationRules.required.message;
        el.isValidated = true;
      }
    });

    if (
      JSON.stringify(validationState) !==
      JSON.stringify(this.state.validationState)
    ) {
      this.setState({ validationState, isFormValid });
    }
    return isFormValid;
  }

  /**
   * Handle form validation.
   * @param {object} state -
   * @return {void}
   */
  manageResponse: (status: Object) => void = status => {
    const success = status === 'ok';
    if (success) {
      const { item, formType } = this.props;
      this.pushGtm(item, formType);
    }
    this.setState({
      formSubmitSuccess: success,
      isLoading: false
    });
    // Push an event on success or failure.
    gtmEventPush({
      method: 'formSubmission',
      args: {
        callbackOrMessage: this.props.formType,
        wasSuccess: success
      }
    });
  };

  pushGtm: (item: Object, formType: string) => void = (item, formType) => {
    if (isWindow && isWindowProp('dataLayer')) {
      window.dataLayer.push(gtmSubmitForm(item, formType));
    }
  };

  /**
   * Handle form submit.
   * @param {object} event - current input's text
   * @return {void}
   */
  handleSubmit: (
    event: SyntheticEvent<HTMLButtonElement>
  ) => Promise<void> = async event => {
    event.preventDefault();
    if (this.checkFormValidity()) {
      recaptchaRef.current.executeAsync().then(captchaToken => {
        const { item, formType } = this.props;
        const { inputValues } = this.state;

        this.setState({ isLoading: true });

        const payload = {
          ...inputValues,
          formType,
          sourceId: item.sourceId,
          url: item.url,
          captchaToken
        };

        const options = {
          method: 'POST',
          url: `/used-cars/api/contact-dealer`,
          headers: {
            'Content-Type': 'application/json'
          },
          data: payload,
          params: process.env.CLASSIFIEDS_SITE
            ? { site: process.env.CLASSIFIEDS_SITE }
            : {},
          json: true
        };
        gtmEventPush({
          method: 'formSubmitButtonClick',
          args: {
            callbackOrMessage: this.props.formType,
            validForm: true
          }
        });

        this.props.dispatch({
          type: Actions.CHECK_POSTCODE,
          postcode: inputValues.postcode
        });

        axios(options).then(response =>
          this.manageResponse(response.data.status)
        );
      });
    }
    // On errors, scroll to the top of the form.
    else {
      gtmEventPush({
        method: 'formSubmitButtonClick',
        args: {
          callbackOrMessage: this.props.formType,
          validForm: false
        }
      });
    }
  };

  resetForm: () => void = () => {
    this.setState({ formSubmitSuccess: null });
  };

  shouldWrapChild: (child: any) => boolean | void = child => {
    // Broadly speaking if not a div/span or a component then we wont be doing any wrapping.
    // i.e. it's an html element like a 'p'
    if (
      child.type === 'div' ||
      child.type === 'span' ||
      typeof child.type === 'function'
    ) {
      return true;
    }
  };

  /**
   * Handle form submit.
   * @param {object} children - Form's children
   * @return {object} childrenWithProps - wrapped inputs;
   */
  wrapFormElements(children: any, extendStyle: any, otherProps: any): any {
    const {
      classWrapperValid,
      classWrapperInValid,
      classWrapperFirst,
      classValidationMessage,
      classWrapperNoValidation
    } = styles;
    const { validationState, inputValues } = this.state;
    return (
      React.Children
        // Filter out null values (conditional fields).
        .toArray(children)
        .map((child, i) => {
          // All inputs gets wrapped in a 'wrapper' div that contains the validation
          // class. Also a label that comes before the input get generated.
          // If any the error message gets attached after the input.
          // div and span elements that have children get recursively wrapped
          if (!this.shouldWrapChild(child)) {
            return child;
          } else if (child.props.children) {
            return React.cloneElement(child, {
              key: i,
              children: this.wrapFormElements(
                child.props.children,
                extendStyle,
                otherProps
              )
            });
          } else {
            const elemName = child.props.name;
            // We need a default value to keep the form inputs as controlled components.
            const emptyValue = child.props.type === 'checkbox' ? false : '';
            const defaultValue = child.props.value || emptyValue;

            let wrapperClass = classWrapperFirst;
            let currentInputState = validationState[elemName];
            if (
              child.props.type === 'radio' ||
              (child.props.type === 'checkbox' && !child.props.required)
            ) {
              wrapperClass = classWrapperNoValidation;
            } else if (
              currentInputState &&
              currentInputState.isValid === true
            ) {
              wrapperClass = classNames(classWrapperFirst, classWrapperValid);
            } else if (
              currentInputState &&
              currentInputState.isValid === false
            ) {
              wrapperClass = classNames(classWrapperFirst, classWrapperInValid);
            }

            return (
              <div className={wrapperClass} key={i}>
                {child.props.label && (
                  <Label
                    content={child.props.label}
                    classLabel={child.props.classLabel}
                  />
                )}
                {React.cloneElement(child, {
                  ...otherProps,
                  checked:
                    child.props.type === 'checkbox'
                      ? inputValues[elemName]
                      : !!child.props.checked,
                  name: elemName,
                  value:
                    elemName in inputValues
                      ? inputValues[elemName]
                      : defaultValue,
                  onChange: child.props.onChange || this.handleOnChange,
                  onBlur: child.props.onBlur || this.handleOnBlur,
                  placeholder:
                    child.props.placeholder && child.props.required
                      ? `${child.props.placeholder} *`
                      : child.props.placeholder
                })}

                {currentInputState && currentInputState.validationMessage ? (
                  <div className={classNames(classValidationMessage)}>
                    {currentInputState.validationMessage}
                  </div>
                ) : null}
              </div>
            );
          }
        })
    );
  }

  /**
   * Render
   * @return {ReactElement} markup
   */
  render(): Element<*> {
    const {
      extendStyle,
      successText,
      errorText,
      title,
      handleSubmit,
      children,
      ...otherProps
    } = this.props;
    delete otherProps['children'];

    const wrappedFormElements = this.wrapFormElements(
      children,
      extendStyle,
      otherProps
    );

    const { classForm, classFormClosed, classFormSpinner } = styles;

    return (
      <div>
        {this.state.isLoading && (
          <div className={classFormSpinner}>
            <Spinner width={50} height={50} />
          </div>
        )}
        {title && <h4>{title}</h4>}
        {this.state.formSubmitSuccess !== null &&
          this.props.submissionRender(
            this.state.formSubmitSuccess,
            this.resetForm
          )}
        <form
          className={classNames(
            extendStyle,
            classForm,
            this.state.formSubmitSuccess === null || classFormClosed
          )}
          noValidate
        >
          {wrappedFormElements}
          <Button
            {...otherProps}
            attributeType="submit"
            buttonText={this.props.buttonText}
            onClick={
              handleSubmit
                ? ev => handleSubmit(ev, this.state)
                : this.handleSubmit
            }
            size={this.props.sizeButton}
          />
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={process.env.RECAPTCHA_CLIENT_KEY}
          />
        </form>
      </div>
    );
  }
}

export { Form };
// $FlowFixMe
export default connect()(Form);



// WEBPACK FOOTER //
// ./src/Common/Form/index.js